import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import { keys, reduce, reduceRight, isEmpty, map, o, assocPath, path, juxt, identity, difference, pluck, head, dissocPath, reduced, tail, over, lensIndex, compose, useWith, __, defaultTo, cond, append, T, includes, prop } from 'ramda';
import { makeStoreInterface, enhanceStore } from '@redux-syringe/injectors';
import { isActionFromNamespace, DEFAULT_FEATURE } from '@redux-syringe/namespaces';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _toPropertyKey from '@babel/runtime/helpers/toPropertyKey';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import { pickFunctions } from '@redux-syringe/utils';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';

// NOTE: Custom implementation so existing keys are always preserved.
var combineReducers = function combineReducers(reducers) {
  var finalReducers = pickFunctions(reducers);
  var finalReducerKeys = keys(finalReducers);
  return function () {
    var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var action = arguments.length > 1 ? arguments[1] : undefined;
    return reduce(function (previousState, reducerKey) {
      var reducer = finalReducers[reducerKey];
      var previousStateForKey = previousState[reducerKey];
      var nextStateForKey = reducer(previousStateForKey, action);

      if (nextStateForKey === previousStateForKey) {
        return previousState;
      }

      return _objectSpread(_objectSpread({}, previousState), {}, _defineProperty({}, reducerKey, nextStateForKey));
    }, state, finalReducerKeys);
  };
};

var composeReducers = function composeReducers() {
  for (var _len = arguments.length, reducers = new Array(_len), _key = 0; _key < _len; _key++) {
    reducers[_key] = arguments[_key];
  }

  return function (state, action) {
    return reduceRight(function (reducer, currentState) {
      return reducer(currentState, action);
    }, state, reducers);
  };
};

var ROOT_KEY = '@redux-syringe/reducers/ROOT_KEY';

var combineReducerSchema = function combineReducerSchema(_ref) {
  var _ref2;

  var rootReducers = _ref[ROOT_KEY],
      otherReducers = _objectWithoutProperties(_ref, [ROOT_KEY].map(_toPropertyKey));

  var resolvedRootReducers = (_ref2 = rootReducers) !== null && _ref2 !== void 0 ? _ref2 : [];

  if (isEmpty(otherReducers)) {
    return composeReducers.apply(void 0, _toConsumableArray(resolvedRootReducers));
  }

  return composeReducers.apply(void 0, _toConsumableArray(resolvedRootReducers).concat([combineReducers(map(combineReducerSchema, otherReducers))]));
};

var filterReducer = function filterReducer(reducer, namespace) {
  return function (state, action) {
    return isActionFromNamespace(namespace, action) ? reducer(state, action) : state;
  };
};

var combineReducerEntries = o(combineReducerSchema, reduce(function (schema, entry) {
  var _entry$feature, _ref;

  var pathDefinition = [].concat(_toConsumableArray(entry.namespace ? [(_entry$feature = entry.feature) !== null && _entry$feature !== void 0 ? _entry$feature : DEFAULT_FEATURE] : []), _toConsumableArray(entry.namespace ? [entry.namespace] : []), _toConsumableArray(entry.path), [ROOT_KEY]);
  return assocPath(pathDefinition, [].concat(_toConsumableArray((_ref = path(pathDefinition, schema)) !== null && _ref !== void 0 ? _ref : []), [filterReducer(entry.value, entry.namespace)]), schema);
}, {}));

var storeInterface = makeStoreInterface('reducers');

var cleanupReducer = function cleanupReducer(state, action) {
  if (action.type !== '@redux-syringe/CLEAN_UP_STATE') {
    return state;
  }

  return reduce(function (previousState, pathDefinition) {
    var _action$meta$feature;

    var fullPathDefinition = [].concat(_toConsumableArray(action.meta.namespace ? [(_action$meta$feature = action.meta.feature) !== null && _action$meta$feature !== void 0 ? _action$meta$feature : DEFAULT_FEATURE] : []), _toConsumableArray(action.meta.namespace ? [action.meta.namespace] : []), _toConsumableArray(pathDefinition)); // GIVEN: fullPathDefinition = ['a', 'b', 'c']
    // THEN: partialPathDefinitions = [['a', 'b', 'c'], ['a', 'b'], ['a']]

    var partialPathDefinitions = reduce(function (previousPartialPathDefinitions, key) {
      var _head;

      return [[].concat(_toConsumableArray((_head = head(previousPartialPathDefinitions)) !== null && _head !== void 0 ? _head : []), [key])].concat(_toConsumableArray(previousPartialPathDefinitions));
    }, [], fullPathDefinition);
    return reduce(function (populatedState, partialPathDefinition) {
      return isEmpty(path(partialPathDefinition, populatedState)) ? dissocPath(partialPathDefinition, populatedState) : reduced(populatedState);
    }, // NOTE: `dissocPath` and `tail` are used here to kick-start the cascading process.
    dissocPath(fullPathDefinition, previousState), tail(partialPathDefinitions));
  }, state, action.payload);
};

var makeEnhancer = function makeEnhancer() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      initialReducers = _ref.initialReducers;

  return function (createStore) {
    return function () {
      var reducer = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : identity;

      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      var prevStore = createStore.apply(void 0, [reducer].concat(args));

      var handleEntriesChanged = function handleEntriesChanged() {
        return nextStore.replaceReducer(composeReducers(reducer, combineReducerEntries(storeInterface.getEntries(nextStore)), cleanupReducer));
      };

      var handleEjected = function handleEjected(_ref2) {
        var entries = _ref2.entries,
            props = _ref2.props;
        var nextEntries = storeInterface.getEntries(nextStore);
        var fullyEjectedEntries = difference(entries, nextEntries);
        nextStore.dispatch({
          type: '@redux-syringe/CLEAN_UP_STATE',
          payload: pluck('path', fullyEjectedEntries),
          meta: props
        });
      };

      var nextStore = enhanceStore(prevStore, storeInterface, {
        onInjected: handleEntriesChanged,
        onEjected: juxt([handleEntriesChanged, handleEjected])
      });

      if (initialReducers) {
        nextStore.injectReducers(initialReducers);
      }

      return nextStore;
    };
  };
};

var isFunction = function isFunction(value) {
  return typeof value === 'function';
};

var isString = function isString(value) {
  return typeof value === 'string';
};

var overHead = over(lensIndex(0));

var createTypeEqualsPredicate = function createTypeEqualsPredicate(condition) {
  return function (state, action) {
    if (isString(condition)) {
      return action.type === condition;
    }

    if (Array.isArray(condition)) {
      return includes(action.type, condition);
    }

    if (isFunction(condition)) {
      return condition(action);
    }

    throw new TypeError('The condition passed to makeReducer must be a string, an array of strings, or a predicate. ' + 'Instead, it received ' + condition + '.');
  };
};

var mergeReducers = function mergeReducers(_ref) {
  var _ref2 = _slicedToArray(_ref, 3),
      typePredicate = _ref2[0],
      reducer = _ref2[1],
      errorReducer = _ref2[2];

  var newReducer = function newReducer(state, action) {
    if (prop('error', action) && errorReducer) {
      return errorReducer(state, action);
    }

    return reducer(state, action);
  };

  return [typePredicate, newReducer];
};
/**
 * @deprecated Use `createReducer` from Redux Toolkit.
 */


var makeReducer = function makeReducer(tuples, initialState) {
  return compose( // eslint-disable-next-line react-hooks/rules-of-hooks
  useWith(__, [defaultTo(initialState), identity]), cond, map(mergeReducers), append([T, identity, identity]), map(overHead(createTypeEqualsPredicate)))(tuples);
};

export { combineReducers, composeReducers, makeEnhancer, makeReducer, storeInterface };
